import { create } from 'zustand';
import midiService from '../services/midiService';

/**
 * Global Application State
 * Using Zustand for state management
 */

const useStore = create((set, get) => ({
    // MIDI Connection State
    midiInitialized: false,
    midiInputs: [],
    midiOutputs: [],
    selectedInputId: null,
    selectedOutputId: null,
    isConnected: false,
    _listenersBound: false, // guard so store-level MIDI listeners are bound once
    connectionStatus: 'disconnected', // 'disconnected' | 'connecting' | 'connected' | 'error'

    // MIDI Messages Log
    midiMessages: [],
    maxMessages: 100,

    // Device State (from Looper Plus)
    deviceState: {
        trackA: {
            status: 'stopped', // 'recording' | 'playing' | 'overdubbing' | 'stopped'
            length: 0,
            volume: 127
        },
        trackB: {
            status: 'stopped',
            length: 0,
            volume: 127
        },
        currentProgram: 0,
        batteryLevel: null,
        memoryUsed: null
    },

    // Current Profile/Configuration
    activeProfileSlot: 1, // 1, 2, or 3
    profiles: {
        1: {
            id: 'slot_1',
            name: 'Profile 1',
            deviceType: 'looper-plus',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            footswitches: {
                fs1: { function: 'rec_play', holdFunction: null, enabled: true },
                fs2: { function: 'stop', holdFunction: null, enabled: true },
                ext1: { function: 'undo_redo', holdFunction: null, enabled: false },
                ext2: { function: 'clear', holdFunction: null, enabled: false }
            },
            encoder: { function: 'volume' },
            midiSettings: {
                channel: 1,
                clockEnabled: false,
                inputSource: 'usb',
                externalControl: true
            },
            trackSettings: {
                mode: 'serial',
                recordingLogic: 'auto',
                overdubMode: 'overdub'
            },
            loopSlots: {}
        },
        2: {
            id: 'slot_2',
            name: 'Profile 2',
            deviceType: 'looper-plus',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            footswitches: {
                fs1: { function: 'rec_play', holdFunction: null, enabled: true },
                fs2: { function: 'stop', holdFunction: null, enabled: true },
                ext1: { function: 'undo_redo', holdFunction: null, enabled: false },
                ext2: { function: 'clear', holdFunction: null, enabled: false }
            },
            encoder: { function: 'volume' },
            midiSettings: {
                channel: 1,
                clockEnabled: false,
                inputSource: 'usb',
                externalControl: true
            },
            trackSettings: {
                mode: 'serial',
                recordingLogic: 'auto',
                overdubMode: 'overdub'
            },
            loopSlots: {}
        },
        3: {
            id: 'slot_3',
            name: 'Profile 3',
            deviceType: 'looper-plus',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            footswitches: {
                fs1: { function: 'rec_play', holdFunction: null, enabled: true },
                fs2: { function: 'stop', holdFunction: null, enabled: true },
                ext1: { function: 'undo_redo', holdFunction: null, enabled: false },
                ext2: { function: 'clear', holdFunction: null, enabled: false }
            },
            encoder: { function: 'volume' },
            midiSettings: {
                channel: 1,
                clockEnabled: false,
                inputSource: 'usb',
                externalControl: true
            },
            trackSettings: {
                mode: 'serial',
                recordingLogic: 'auto',
                overdubMode: 'overdub'
            },
            loopSlots: {}
        }
    },
    // Initialize currentProfile with Slot 1's data
    currentProfile: {
        id: 'slot_1',
        name: 'Profile 1',
        deviceType: 'looper-plus',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        footswitches: {
            fs1: { function: 'rec_play', holdFunction: null, enabled: true },
            fs2: { function: 'stop', holdFunction: null, enabled: true },
            ext1: { function: 'undo_redo', holdFunction: null, enabled: false },
            ext2: { function: 'clear', holdFunction: null, enabled: false }
        },
        encoder: { function: 'volume' },
        midiSettings: {
            channel: 1,
            clockEnabled: false,
            inputSource: 'usb',
            externalControl: true
        },
        trackSettings: {
            mode: 'serial',
            recordingLogic: 'auto',
            overdubMode: 'overdub'
        },
        loopSlots: {}
    },
    profileModified: false,

    // Device Type Selection
    deviceType: 'looper-plus', // 'looper-plus' | 'looper-x'
    setDeviceType: (type) => set({ deviceType: type }),

    // Actions
    initMIDI: async () => {
        set({ connectionStatus: 'connecting' });
        const result = await midiService.init();

        if (result.success) {
            const inputs = midiService.inputs;
            const outputs = midiService.outputs;

            // Keep port lists in sync when devices are hot-plugged/unplugged
            // (bound once here — midiService.addListener does NOT dedupe).
            if (!get()._portsListenerBound) {
                midiService.addListener('portschanged', () => get().refreshPorts());
                set({ _portsListenerBound: true });
            }

            set({
                midiInitialized: true,
                midiInputs: inputs,
                midiOutputs: outputs,
                connectionStatus: 'disconnected'
            });
        } else {
            set({ connectionStatus: 'error' });
        }
    },

    refreshPorts: () => {
        midiService.refreshPorts();
        set({
            midiInputs: midiService.inputs,
            midiOutputs: midiService.outputs
        });
    },

    autoConnect: () => {
        // Refresh first to ensure latest list
        midiService.refreshPorts();

        const { input, output, name } = midiService.findLooperPorts();

        if (input && output) {
            const result = get().connectMIDI(input, output);
            if (result.success) {
                return { success: true, name, portName: name };
            }
            return { success: false, error: result.error };
        }

        set({
            midiInputs: midiService.inputs,
            midiOutputs: midiService.outputs
        });

        return { success: false };
    },

    connectMIDI: (inputId, outputId) => {
        const result = midiService.connect(inputId, outputId);

        if (result.success) {
            set({
                selectedInputId: inputId,
                selectedOutputId: outputId,
                isConnected: true,
                connectionStatus: 'connected'
            });

            // Setup MIDI message listeners ONCE (app-level, not connection-specific).
            // Re-binding on every connect would register duplicate closures into
            // midiService.listeners and fire addMIDIMessage multiple times per message.
            if (!get()._listenersBound) {
                midiService.addListener('noteon', (data) => {
                    get().addMIDIMessage('noteon', data);
                });

                midiService.addListener('controlchange', (data) => {
                    get().addMIDIMessage('controlchange', data);
                });

                midiService.addListener('programchange', (data) => {
                    get().addMIDIMessage('programchange', data);
                    set({ deviceState: { ...get().deviceState, currentProgram: data.program } });
                });

                set({ _listenersBound: true });
            }
        } else {
            // Deliberately NOT connectionStatus 'error': the UI banner renders
            // that as "Web MIDI API not available", which would misreport a
            // vanished device. The caller surfaces result.error instead.
            console.error('❌ MIDI connect failed:', result.error);
            set({ isConnected: false, connectionStatus: 'disconnected' });
        }

        return result;
    },

    disconnectMIDI: () => {
        midiService.disconnect();
        set({
            selectedInputId: null,
            selectedOutputId: null,
            isConnected: false,
            connectionStatus: 'disconnected'
        });
    },

    addMIDIMessage: (type, data) => {
        const messages = get().midiMessages;
        const newMessage = {
            id: typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type,
            ...data, // Spread data for direct access in Monitor
            data,    // Keep data object for specific checking if needed (like direction)
            timestamp: new Date()
        };

        const updatedMessages = [newMessage, ...messages].slice(0, get().maxMessages);
        set({ midiMessages: updatedMessages });
    },

    clearMIDIMessages: () => {
        set({ midiMessages: [] });
    },

    // Send MIDI commands
    sendNoteOn: (note, velocity, channel) => {
        const success = midiService.sendNoteOn(note, velocity, channel);
        if (success) {
            get().addMIDIMessage('noteon', { note, velocity, channel, direction: 'out' });
        }
        return success;
    },

    sendNoteOff: (note, channel) => {
        const success = midiService.sendNoteOff(note, channel);
        if (success) {
            // Note Off usually has velocity 0 or is a distinct event
            get().addMIDIMessage('noteoff', { note, velocity: 0, channel, direction: 'out' });
        }
        return success;
    },

    sendControlChange: (controller, value, channel) => {
        const success = midiService.sendControlChange(controller, value, channel);
        if (success) {
            get().addMIDIMessage('controlchange', { controller, value, channel, direction: 'out' });
        }
        return success;
    },

    sendProgramChange: (program, channel) => {
        const success = midiService.sendProgramChange(program, channel);
        if (success) {
            get().addMIDIMessage('programchange', { program, channel, direction: 'out' });
        }
        return success;
    },

    // Update device state
    updateDeviceState: (newState) => {
        set({ deviceState: { ...get().deviceState, ...newState } });
    },

    // Profile management
    setCurrentProfile: (profile) => {
        // When setting current profile, also sync it to the active slot in storage
        const activeSlot = get().activeProfileSlot;
        const updatedProfiles = {
            ...get().profiles,
            [activeSlot]: profile
        };

        set({
            currentProfile: profile,
            profiles: updatedProfiles,
            profileModified: false
        });
    },

    switchProfileSlot: (slotId) => {
        const state = get();
        const targetProfile = state.profiles[slotId];

        if (!targetProfile || slotId === state.activeProfileSlot) return;

        // Preserve unsaved edits: sync the working profile into the OLD slot
        // before switching, so nothing the user changed is silently discarded.
        const profiles = {
            ...state.profiles,
            [state.activeProfileSlot]: { ...state.currentProfile }
        };

        set({
            profiles,
            activeProfileSlot: slotId,
            currentProfile: { ...targetProfile }, // Clone to avoid direct mutation issues
            profileModified: false
        });
    },

    setProfileModified: (modified) => {
        set({ profileModified: modified });
    }
}));

export default useStore;
